import {
  DndContext,
  DragEndEvent,
  DragMoveEvent,
  DragOverEvent,
  DragStartEvent,
  PointerSensor,
  UniqueIdentifier,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useEffect, useMemo, useRef } from 'react';

import { Contract } from 'components/molecules';
import { useFormikContext } from 'formik';
import { t } from 'i18next';
import toast from 'react-hot-toast';
import {
  AGREEMENT_TYPE,
  IAgreementTemplatetAggregatorForm,
} from 'services/templates/agreement-template/types';
import {
  IContractFormikStatus,
  IContractSideMenuForm,
  IContractTemplateDetailItemForm,
  IContractTemplateTreeItemForm,
  IDistributionType,
  SIDE_MENU_TYPE,
  TEMPLATE_TYPE,
  type IContractTemplateFlattenedTreeForm,
  type IContractTemplateForm,
  type SensorContext,
} from 'services/templates/types';
import {
  addItem,
  buildTree,
  findLowestAvailableId,
  flattenTree,
  getProjection,
  measuring,
  removeChildrenOf,
  removeItem,
  setProperty,
} from 'utils/dnd/dndUtilities';
import ContractSortableListItems from '../elements/list-items/ContractSortableListItems';
import ContractSideMenu from '../elements/side-menu/ContractSideMenu';

const ContractFormTab = () => {
  const { values, setFieldValue, status, initialStatus } =
    useFormikContext<IContractTemplateForm>();

  const { namePrefix, hideContract }: IContractFormikStatus = status;

  const { items, activeId, overId, offsetLeft, sideMenu } = values;

  const flattenedItems = useMemo(() => {
    const flattenedTree = flattenTree(items);

    const collapsedItems = flattenedTree.reduce<string[]>(
      (acc, { children, collapsed, id }) => {
        return collapsed && children.length ? [...acc, id] : acc;
      },
      []
    );

    return removeChildrenOf(
      flattenedTree,
      activeId ? [activeId, ...collapsedItems] : collapsedItems
    );
  }, [activeId, items]);

  const assignItems = (newItems: IContractTemplateTreeItemForm[]) =>
    setFieldValue(`${namePrefix}`, newItems);

  const isOverMainItem = (overId: UniqueIdentifier) =>
    flattenedItems.find(
      item => item.type === TEMPLATE_TYPE.contract && item.id === `${overId}`
    );

  const getProjected = () =>
    activeId && overId
      ? getProjection(
          flattenedItems,
          activeId,
          overId,
          offsetLeft,
          initialStatus.indentationWidth,
          initialStatus.minDepth
        )
      : null;

  const sensorContext: SensorContext = useRef({
    items: flattenedItems,
    offset: offsetLeft,
  });

  const sensors = useSensors(useSensor(PointerSensor));

  const sortedIds = useMemo(
    () => flattenedItems.map(({ id }) => id),
    [flattenedItems]
  );

  useEffect(() => {
    sensorContext.current = {
      items: flattenedItems,
      offset: offsetLeft,
    };
  }, [flattenedItems, offsetLeft]);

  const handleDragStart = ({ active: { id: activeId } }: DragStartEvent) => {
    setFieldValue('activeId', `${activeId}`);
    setFieldValue('overId', `${activeId}`);

    document.body.style.setProperty('cursor', 'grabbing');
  };

  const handleDragMove = ({ delta }: DragMoveEvent) => {
    setFieldValue('offsetLeft', delta.x);
  };

  const handleDragOver = ({ over }: DragOverEvent) => {
    over && !isOverMainItem(over.id)
      ? setFieldValue('overId', `${over.id}`)
      : setFieldValue('overId', null);
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    const digitalDistribution = flattenedItems.find(
      item => item.type === TEMPLATE_TYPE.digitalDistribution
    );
    const merchDistribution = flattenedItems.find(
      item => item.type === TEMPLATE_TYPE.merchDistribution
    );
    const physicalDistribution = flattenedItems.find(
      item => item.type === TEMPLATE_TYPE.physicalDistribution
    );
    const channelDistribution = flattenedItems.find(
      item => item.type === TEMPLATE_TYPE.channelDistribution
    );
    const activeItem = flattenedItems.find(item => item.id === active.id);
    const overItem = flattenedItems.find(item => item.id === over?.id);

    const projected = getProjected();
    resetState();
    if (projected && over) {
      if (isOverMainItem(over.id)) {
        return toast.error(t('toast-message.contract-item-above-main-title'));
      }
      if (
        activeItem?.type === TEMPLATE_TYPE.agreement &&
        projected.depth <= 1
      ) {
        return toast.error(
          t('toast-message.agreement-must-be-placed-under-section')
        );
      }
      if (projected.depth < initialStatus.minDepth) {
        return toast.error(
          t('toast-message.contract-item-blocked-to-move-in-that-place')
        );
      }

      if (
        (projected.parentId === digitalDistribution?.id ||
          projected.parentId === physicalDistribution?.id ||
          projected.parentId === merchDistribution?.id ||
          projected.parentId === channelDistribution?.id) &&
        ((activeItem?.type === TEMPLATE_TYPE.agreement &&
          activeItem?.content.type !== AGREEMENT_TYPE.shares) ||
          activeItem?.type !== TEMPLATE_TYPE.agreement)
      ) {
        return toast.error(
          t(
            'toast-message.only-shares-agreement-can-be-under-digital-distribution-section'
          )
        );
      }
      if (active.id === digitalDistribution?.id && projected.depth !== 1) {
        return toast.error(
          t('toast-message.digital-distribution-section-cannot-be-nested')
        );
      }
      if (
        !(
          projected.parentId === digitalDistribution?.id ||
          projected.parentId === physicalDistribution?.id ||
          projected.parentId === merchDistribution?.id ||
          projected.parentId === channelDistribution?.id
        ) &&
        activeItem?.type === TEMPLATE_TYPE.agreement &&
        activeItem?.content.type === AGREEMENT_TYPE.shares
      ) {
        return toast.error(
          t(
            'toast-message.only-shares-agreement-can-be-under-digital-distribution-section'
          )
        );
      }

      if (
        !isOverMainItem(over.id) &&
        projected.depth >= initialStatus.minDepth
      ) {
        const { depth, parentId } = projected;

        const clonedItems: IContractTemplateFlattenedTreeForm[] = JSON.parse(
          JSON.stringify(flattenTree(items))
        );
        const overIndex = clonedItems.findIndex(({ id }) => id === over.id);
        const activeIndex = clonedItems.findIndex(({ id }) => id === active.id);
        const activeTreeItem = clonedItems[activeIndex];

        clonedItems[activeIndex] = { ...activeTreeItem, depth, parentId };

        const sortedItems = arrayMove(clonedItems, activeIndex, overIndex);

        const newItems = buildTree(sortedItems);

        assignItems(newItems);
      }
    }
  };

  const handleDragCancel = () => {
    resetState();
  };

  const resetState = () => {
    setFieldValue('overId', null);
    setFieldValue('activeId', null);
    setFieldValue('offsetLeft', 0);

    document.body.style.setProperty('cursor', '');
  };

  const onDisplayDetail = (
    id: string,
    content: IAgreementTemplatetAggregatorForm
  ) => {
    const newDetail: IContractTemplateDetailItemForm = {
      id,
      content,
    };

    const sideMenu: IContractSideMenuForm = {
      displayed: true,
      type: SIDE_MENU_TYPE.agreementDetail,
      detail: newDetail,
    };
    setFieldValue('sideMenu', sideMenu);
  };

  const onRemove = (id: string) => {
    if (id === sideMenu?.detail?.id) {
      setFieldValue('detail', null);
    }
    assignItems(removeItem(items, id));
  };

  const onAddShare = (parentId: string) => {
    assignItems(
      addItem(items, parentId, {
        children: [],
        content: {
          selectAgreementTemplate: null,
          textAgreementTemplate: null,
          distributionTemplateShares: { id: '', name: '' },
          type: AGREEMENT_TYPE.shares,
        },
        type: TEMPLATE_TYPE.agreement,
        id: findLowestAvailableId(items),
      })
    );
  };

  const onAddDistribution = (parentId: string, type: IDistributionType) => {
    assignItems(
      addItem(items, parentId, {
        children: [
          {
            id: `default-share-${TEMPLATE_TYPE[type]}`,
            type: TEMPLATE_TYPE.agreement,
            children: [],
            content: {
              type: AGREEMENT_TYPE.defaultShare,
              distributionTemplateShares: {
                id: `default-share-agreement-${TEMPLATE_TYPE[type]}`,
                name: t('contract-template.digitalDistribution.default-share'),
              },
            },
            disabledSelection: true,
          },
        ],
        content: t(`contract-template.${TEMPLATE_TYPE[type]}.title`),
        type: type,
        id: findLowestAvailableId(items),
      })
    );
  };

  const onAddDigitalDistribution = (parentId: string) =>
    onAddDistribution(parentId, TEMPLATE_TYPE.digitalDistribution);

  const onAddphysicalDistribution = (parentId: string) =>
    onAddDistribution(parentId, TEMPLATE_TYPE.physicalDistribution);

  const onAddMerchDistribution = (parentId: string) =>
    onAddDistribution(parentId, TEMPLATE_TYPE.merchDistribution);

  const onAddYoutubelDistribution = (parentId: string) =>
    onAddDistribution(parentId, TEMPLATE_TYPE.channelDistribution);

  const onAddCustomDistribution = (
    parentId: string,
    type: IDistributionType
  ) => {
    const sideMenu: IContractSideMenuForm = {
      type: SIDE_MENU_TYPE.customTemplatesList,
      displayed: true,
      customTemplate: {
        parentId: parentId,
        search: '',
        selectedId: '',
        type: type,
      },
    };
    setFieldValue('sideMenu', sideMenu);
  };

  const onAddCustomDigitalDistribution = (parentId: string) =>
    onAddCustomDistribution(parentId, TEMPLATE_TYPE.digitalDistribution);

  const onAddCustomPhysicalDistribution = (parentId: string) =>
    onAddCustomDistribution(parentId, TEMPLATE_TYPE.physicalDistribution);

  const onAddCustomMerchDistribution = (parentId: string) =>
    onAddCustomDistribution(parentId, TEMPLATE_TYPE.merchDistribution);

  const onAddCustomChannelDistribution = (parentId: string) =>
    onAddCustomDistribution(parentId, TEMPLATE_TYPE.channelDistribution);

  const onAddCondition = (parentId: string) => {
    assignItems(
      addItem(items, parentId, {
        children: [],
        content: {
          selectAgreementTemplate: {
            defaultValue: '',
            title: '',
            options: [],
          },
          textAgreementTemplate: { defaultValue: '', title: '' },
          type: AGREEMENT_TYPE.text,
        },
        type: TEMPLATE_TYPE.agreement,
        id: findLowestAvailableId(items),
      })
    );
  };

  const onAddCustomCondition = (parentId: string) => {
    const sideMenu: IContractSideMenuForm = {
      type: SIDE_MENU_TYPE.customTemplatesList,
      displayed: true,
      customTemplate: {
        parentId: parentId,
        search: '',
        selectedId: '',
        type: TEMPLATE_TYPE.agreement,
      },
    };
    setFieldValue('sideMenu', sideMenu);
  };

  const onAddSection = (parentId: string) => {
    assignItems(
      addItem(items, parentId, {
        children: [],
        content: '',
        type: TEMPLATE_TYPE.section,
        id: findLowestAvailableId(items),
      })
    );
  };

  const onAddCustomSection = (parentId: string) => {
    const sideMenu: IContractSideMenuForm = {
      type: SIDE_MENU_TYPE.customTemplatesList,
      displayed: true,
      customTemplate: {
        parentId: parentId,
        search: '',
        selectedId: '',
        type: TEMPLATE_TYPE.section,
      },
    };
    setFieldValue('sideMenu', sideMenu);
  };

  const onAddCustomContract = () => {
    const sideMenu: IContractSideMenuForm = {
      type: SIDE_MENU_TYPE.customTemplatesList,
      displayed: true,
      customTemplate: {
        parentId: '0',
        search: '',
        selectedId: '',
        type: TEMPLATE_TYPE.contract,
      },
    };
    setFieldValue('sideMenu', sideMenu);
  };

  const handleCollapse = (id: string) => {
    assignItems(setProperty(items, id, 'collapsed', value => !value));
  };

  return (
    <div className='flex justify-center w-full h-max'>
      {!hideContract && (
        <Contract.Page>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            measuring={measuring}
            onDragStart={handleDragStart}
            onDragMove={handleDragMove}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}>
            <SortableContext
              items={sortedIds}
              strategy={verticalListSortingStrategy}>
              <ContractSortableListItems
                flattenedItems={flattenedItems}
                activeId={activeId}
                projected={getProjected()}
                onAddCondition={onAddCondition}
                onAddCustomDigitalDistribution={onAddCustomDigitalDistribution}
                onAddCustomPhysicalDistribution={
                  onAddCustomPhysicalDistribution
                }
                onAddCustomMerchDistribution={onAddCustomMerchDistribution}
                onAddCustomChannelDistribution={onAddCustomChannelDistribution}
                onAddMerchDistribution={onAddMerchDistribution}
                onAddChannelDistribution={onAddYoutubelDistribution}
                onAddPhysicalDistribution={onAddphysicalDistribution}
                onAddCustomContract={onAddCustomContract}
                onAddShare={onAddShare}
                onAddCustomSection={onAddCustomSection}
                onAddDigitalDistribution={onAddDigitalDistribution}
                onAddSection={onAddSection}
                onAddCustomCondition={onAddCustomCondition}
                onDisplayDetail={onDisplayDetail}
                onRemove={onRemove}
                handleCollapse={handleCollapse}
              />
            </SortableContext>
          </DndContext>
        </Contract.Page>
      )}
      <ContractSideMenu />
    </div>
  );
};

export default ContractFormTab;
